import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/GridLegacy';
import { useContext, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';

import { useRetrieveAccessTokenQuery } from '../../apiClients/identityClient/requests/AccessTokenApi';
import type { ErrorResponse } from '../../apiClients/types';
import { LOGIN_ERROR_ROUTE, LOGIN_ORG_ROUTE } from '../../constants';
import { clearLoginError } from '../../store/localStorageHelpers/loginErrorHelper';
import { getCSRFState } from '../../store/sessionStorageHelpers/authStateHelpers';
import { Background } from '../background/Background';
import { LoginContext } from '../loginHandler/LoginContext';

interface RetrieveAccessTokenProps {
    authCode: string;
    includeClientSecret: boolean;
    forceOauthPrefix: boolean;
}

export function RetrieveAccessToken({
    authCode,
    includeClientSecret,
    forceOauthPrefix,
}: RetrieveAccessTokenProps) {
    const navigate = useNavigate();

    const { data, error } = useRetrieveAccessTokenQuery({
        authCode,
        includeClientSecret,
        forceOauthPrefix,
    });

    useEffect(() => {
        if (data) {
            navigate(LOGIN_ORG_ROUTE);
        }
        if (error) {
            const errorMessage =
                (error as ErrorResponse)?.data?.error || 'unable_to_get_access_token';
            const errorDescription = (error as ErrorResponse)?.data?.error_description || '';
            navigate(
                `${LOGIN_ERROR_ROUTE}?error=${errorMessage}&error_description=${errorDescription}`,
            );
        }
    }, [data, error]);

    return (
        <Background>
            <Grid
                item
                style={{
                    position: 'relative',
                    width: '100%',
                    height: '100px',
                }}
                alignContent="center"
                justifyContent="center"
                display="grid"
            >
                <CircularProgress />
            </Grid>
        </Background>
    );
}

export function LoginSuccess() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const { includeClientSecret, forceOauthPrefix } = useContext(LoginContext);
    const authCode = searchParams.get('code');
    const state = searchParams.get('state');
    const storedState = getCSRFState();

    clearLoginError();

    useEffect(() => {
        // If state values don't match. Then it is possibly a CSRF attack. so hard bail out of the login flow
        if (state !== storedState) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=invalid_auth_state`);
        }
    }, []);

    useEffect(() => {
        if (!authCode) {
            navigate(`${LOGIN_ERROR_ROUTE}?error=no_auth_token`);
        }
    }, [authCode]);

    return (
        <>
            {authCode && state === storedState && (
                <RetrieveAccessToken
                    authCode={authCode}
                    includeClientSecret={includeClientSecret}
                    forceOauthPrefix={forceOauthPrefix}
                />
            )}
        </>
    );
}
